import { useEffect, useRef, useState } from 'react';
import { Loader2, X, Upload, Plus, Trash2, Save } from 'lucide-react';
import { fetchFileBytes, reassembleFile, type UploadedFileDto } from '../../lib/vorgangApi';
import { renderPdfThumbnails, type PageThumb } from '../../lib/pdfThumbnails';

type SeqSrc = 'self' | 'file' | 'upload';
interface SeqItem { uid: string; src: SeqSrc; fileId?: number; page: number; thumb: string }

let _uid = 0;
const nextUid = () => `it-${++_uid}`;

/** Payload, das per dataTransfer übertragen wird. */
type DragPayloadSeq = { kind: 'seq'; uid: string };
type DragPayloadSrc = { kind: 'src'; page: number; thumb: string };
type DragPayload = DragPayloadSeq | DragPayloadSrc;
const DND_TYPE = 'application/x-vn-page';

/**
 * Seiten-Editor (Schritt 3): zeigt die Seiten der Zieldatei und erlaubt das Einfügen
 * von Seiten an EINER konkreten Position — Quelle: ein neu hochgeladenes PDF ODER eine
 * vorhandene Datei. Speichern baut die Datei neu zusammen (ersetzt, Rückgängig via undo).
 * Drag & Drop: Seiten innerhalb der Ziel-Seitenfolge verschieben ODER von der Quelle
 * an eine Position in der Zielfolge ziehen.
 */
export function PageEditor({
  file,
  otherFiles,
  onClose,
}: {
  file: UploadedFileDto;
  otherFiles: UploadedFileDto[];
  onClose: () => void;
}) {
  const [seq, setSeq] = useState<SeqItem[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // Quelle
  const [srcKind, setSrcKind] = useState<'upload' | number | null>(null);
  const [srcThumbs, setSrcThumbs] = useState<PageThumb[] | null>(null);
  const [srcLoading, setSrcLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [pick, setPick] = useState<{ page: number; thumb: string } | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  // Drag-State
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [dragSeqUid, setDragSeqUid] = useState<string | null>(null); // welche seq-Karte wird gezogen
  const dragPayloadRef = useRef<DragPayload | null>(null);

  // Zieldatei-Seiten laden
  useEffect(() => {
    let cancelled = false;
    fetchFileBytes(file.id)
      .then(renderPdfThumbnails)
      .then(t => { if (!cancelled) setSeq(t.map(x => ({ uid: nextUid(), src: 'self' as const, fileId: file.id, page: x.page, thumb: x.dataUrl }))); })
      .catch((e) => { console.error('PageEditor laden:', e); if (!cancelled) setLoadErr('Seiten konnten nicht geladen werden: ' + (e?.message || 'Fehler')); });
    return () => { cancelled = true; };
  }, [file.id]);

  const loadSourceFromBytes = async (bytes: ArrayBuffer) => {
    setSrcLoading(true); setSrcThumbs(null); setPick(null);
    try { setSrcThumbs(await renderPdfThumbnails(bytes)); }
    catch { setSrcThumbs([]); }
    finally { setSrcLoading(false); }
  };

  const onPickUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    // Neuer Upload ersetzt einen evtl. vorherigen → bereits eingefügte Upload-Seiten entfernen.
    setSeq(s => s ? s.filter(it => it.src !== 'upload') : s);
    setUploadFile(f);
    setSrcKind('upload');
    await loadSourceFromBytes(await f.arrayBuffer());
  };

  const onPickExisting = async (fileId: number) => {
    setSrcKind(fileId);
    await loadSourceFromBytes(await fetchFileBytes(fileId));
  };

  const insertAt = (index: number) => {
    if (!pick || srcKind == null) return;
    const item: SeqItem = srcKind === 'upload'
      ? { uid: nextUid(), src: 'upload', page: pick.page, thumb: pick.thumb }
      : { uid: nextUid(), src: 'file', fileId: srcKind, page: pick.page, thumb: pick.thumb };
    setSeq(s => { if (!s) return s; const c = [...s]; c.splice(index, 0, item); return c; });
  };

  const removeAt = (uid: string) => setSeq(s => s ? s.filter(it => it.uid !== uid) : s);

  const save = async () => {
    if (!seq || seq.length === 0) { setSaveErr('Mindestens eine Seite muss bleiben.'); return; }
    setSaving(true); setSaveErr(null);
    try {
      const pages = seq.map(it => it.src === 'upload'
        ? { upload: true, page: it.page }
        : { fileId: it.fileId, page: it.page });
      const needsUpload = seq.some(it => it.src === 'upload');
      await reassembleFile(file.id, pages, needsUpload ? uploadFile : null);
      onClose();
    } catch (e: any) {
      setSaveErr(e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  // --- Drag-Handler für seq-Thumbnails ---
  const handleSeqDragStart = (e: React.DragEvent, uid: string) => {
    e.dataTransfer.effectAllowed = 'move';
    const payload: DragPayloadSeq = { kind: 'seq', uid };
    dragPayloadRef.current = payload;
    e.dataTransfer.setData(DND_TYPE, JSON.stringify(payload));
    setDragSeqUid(uid);
  };

  const handleSeqDragEnd = () => {
    setDragSeqUid(null);
    setDragOverIndex(null);
    dragPayloadRef.current = null;
  };

  // --- Drag-Handler für src-Thumbnails ---
  const handleSrcDragStart = (e: React.DragEvent, t: PageThumb) => {
    e.dataTransfer.effectAllowed = 'copy';
    const payload: DragPayloadSrc = { kind: 'src', page: t.page, thumb: t.dataUrl };
    dragPayloadRef.current = payload;
    e.dataTransfer.setData(DND_TYPE, JSON.stringify(payload));
  };

  const handleSrcDragEnd = () => {
    setDragOverIndex(null);
    dragPayloadRef.current = null;
  };

  // --- Drop-Zone (InsertMarker) Handler ---
  const handleMarkerDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleMarkerDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleMarkerDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragSeqUid(null);

    let payload: DragPayload | null = null;
    try {
      const raw = e.dataTransfer.getData(DND_TYPE);
      if (raw) payload = JSON.parse(raw);
    } catch { /* ignore */ }
    // Fallback auf ref (innerhalb desselben Fensters immer verfügbar)
    if (!payload) payload = dragPayloadRef.current;
    dragPayloadRef.current = null;

    if (!payload) return;

    if (payload.kind === 'seq') {
      // Innerhalb der seq umsortieren
      const { uid } = payload;
      setSeq(s => {
        if (!s) return s;
        const arr = [...s];
        const from = arr.findIndex(it => it.uid === uid);
        if (from < 0) return s;
        // Ziel-Index nach Entfernen des Elements anpassen
        const [moved] = arr.splice(from, 1);
        const insertIdx = from < index ? index - 1 : index;
        arr.splice(insertIdx, 0, moved);
        return arr;
      });
    } else {
      // Quelle → Ziel: neue SeqItem an index einfügen
      const { page, thumb } = payload;
      if (srcKind == null) return;
      const item: SeqItem = srcKind === 'upload'
        ? { uid: nextUid(), src: 'upload', page, thumb }
        : { uid: nextUid(), src: 'file', fileId: srcKind as number, page, thumb };
      setSeq(s => {
        if (!s) return s;
        const c = [...s];
        c.splice(index, 0, item);
        return c;
      });
    }
  };

  const pdfOthers = otherFiles.filter(o => o.id !== file.id && /pdf/i.test(o.mimeType || '') );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Kopf */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">Seiten bearbeiten: {file.originalName}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">{seq ? `${seq.length} Seite(n)` : 'lädt…'} · Quelle wählen, Seite anklicken, dann auf ＋ klicken — oder per Drag & Drop verschieben</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-5">
          {/* Ziel-Seitenfolge */}
          <div>
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Seiten der Datei (Reihenfolge)</p>
            {loadErr && <p className="text-sm text-red-600 dark:text-red-400">{loadErr}</p>}
            {!seq && !loadErr && <div className="flex items-center gap-2 text-slate-500 text-sm py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Seiten werden geladen…</div>}
            {seq && (
              <div className="flex flex-wrap items-stretch gap-1">
                {seq.map((it, i) => (
                  <div key={it.uid} className={`flex items-stretch transition-[padding] duration-150 ${dragOverIndex === i ? 'pl-14' : ''}`}>
                    <InsertMarker
                      active={!!pick}
                      highlighted={dragOverIndex === i}
                      onClick={() => insertAt(i)}
                      onDragOver={e => handleMarkerDragOver(e, i)}
                      onDragLeave={handleMarkerDragLeave}
                      onDrop={e => handleMarkerDrop(e, i)}
                    />
                    <div
                      className={`relative group w-[110px] transition-opacity ${dragSeqUid === it.uid ? 'opacity-40' : 'opacity-100'}`}
                      draggable
                      onDragStart={e => handleSeqDragStart(e, it.uid)}
                      onDragEnd={handleSeqDragEnd}
                      onDragOver={e => handleMarkerDragOver(e, i)}
                      onDragLeave={handleMarkerDragLeave}
                      onDrop={e => handleMarkerDrop(e, i)}
                    >
                      <img
                        src={it.thumb}
                        alt={`Seite ${i + 1}`}
                        className={`w-full border rounded shadow-sm bg-white cursor-grab active:cursor-grabbing ${
                          dragOverIndex === i
                            ? 'border-blue-500 ring-2 ring-blue-400'
                            : it.src !== 'self'
                            ? 'border-blue-400 ring-1 ring-blue-300'
                            : 'border-slate-200 dark:border-slate-700'
                        }`}
                      />
                      <span className="absolute bottom-0 left-0 right-0 text-[12px] text-center bg-black/50 text-white rounded-b">{i + 1}{it.src !== 'self' ? ' · neu' : ''}</span>
                      <button onClick={() => removeAt(it.uid)} title="Seite entfernen" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-500 hover:text-red-600 grid place-items-center opacity-0 group-hover:opacity-100 transition"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
                <InsertMarker
                  active={!!pick}
                  highlighted={dragOverIndex === seq.length}
                  onClick={() => insertAt(seq.length)}
                  onDragOver={e => handleMarkerDragOver(e, seq.length)}
                  onDragLeave={handleMarkerDragLeave}
                  onDrop={e => handleMarkerDrop(e, seq.length)}
                />
              </div>
            )}
          </div>

          {/* Quelle */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Seite(n) zum Einfügen</p>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700">
                <Upload className="w-4 h-4" /> PDF hochladen
                <input type="file" accept="application/pdf" className="hidden" onChange={onPickUpload} />
              </label>
              {pdfOthers.length > 0 && (
                <select
                  value={typeof srcKind === 'number' ? srcKind : ''}
                  onChange={e => e.target.value && onPickExisting(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                >
                  <option value="">…oder vorhandene Datei wählen</option>
                  {pdfOthers.map(o => <option key={o.id} value={o.id}>{o.originalName} ({o.category ?? '—'})</option>)}
                </select>
              )}
              {pick && <span className="text-xs text-blue-600 dark:text-blue-300">Seite {pick.page} gewählt → auf ein ＋ klicken oder in die Sequenz ziehen</span>}
            </div>

            {srcLoading && <div className="flex items-center gap-2 text-slate-500 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" />Quelle wird geladen…</div>}
            {srcThumbs && srcThumbs.length === 0 && <p className="text-sm text-slate-500">Keine Seiten in der Quelle.</p>}
            {srcThumbs && srcThumbs.length > 0 && (
              <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(96px,1fr))' }}>
                {srcThumbs.map(t => (
                  <button
                    key={t.page}
                    draggable
                    onDragStart={e => handleSrcDragStart(e, t)}
                    onDragEnd={handleSrcDragEnd}
                    onClick={() => setPick({ page: t.page, thumb: t.dataUrl })}
                    className={`relative rounded border cursor-grab active:cursor-grabbing ${pick?.page === t.page ? 'border-blue-500 ring-2 ring-blue-300' : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'}`}
                    title={`Seite ${t.page} wählen oder in die Sequenz ziehen`}
                  >
                    <img src={t.dataUrl} alt={`Quelle Seite ${t.page}`} className="w-full rounded bg-white" />
                    <span className="absolute bottom-0 left-0 right-0 text-[12px] text-center bg-black/50 text-white rounded-b">{t.page}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Fuß */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
          <span className="text-xs text-slate-500 dark:text-slate-400">{saveErr ? <span className="text-red-600">{saveErr}</span> : 'Die alte Fassung bleibt als Rückgängig (↩) erhalten.'}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">Abbrechen</button>
            <button onClick={save} disabled={saving || !seq} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Speichern</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** „＋"-Einfügemarker zwischen den Seiten. Hervorgehoben, wenn eine Quell-Seite gewählt ist
 *  oder eine Seite per Drag & Drop drüber gezogen wird. */
function InsertMarker({
  active,
  highlighted,
  onClick,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  active: boolean;
  highlighted: boolean;
  onClick: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  return (
    <button
      onClick={onClick}
      title={active ? 'Gewählte Seite hier einfügen' : 'Erst eine Quell-Seite wählen oder hierher ziehen'}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`w-6 self-stretch my-1 rounded grid place-items-center transition ${
        highlighted
          ? 'bg-blue-200 dark:bg-blue-700 text-blue-700 dark:text-blue-200 scale-x-125'
          : active
          ? 'text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer'
          : 'text-slate-300 dark:text-slate-600 cursor-default'
      }`}
    >
      <Plus className="w-4 h-4" />
    </button>
  );
}
